<template>
  <v-container fluid>
    <!-- Active Filter Controls (similar to Home.vue) -->
    <v-card class="pa-4 mb-4 elevation-2" outlined>
      <div class="d-flex justify-end mb-2">
        <v-btn text small class="clear-btn-ux" @click="clearFilters">
          <v-icon left small color="grey">mdi-filter-remove</v-icon>
          <span class="clear-btn-label">Clear</span>
        </v-btn>
      </div>
      <v-row dense>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.insurer"
            :items="insurersForFilter"
            item-text="name"
            item-value="id"
            label="Insurer"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onFilterChange"
            prepend-icon="mdi-domain"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.branch"
            :items="branchesForFilter"
            item-text="name"
            item-value="id"
            label="Branch"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onBranchChange"
            prepend-icon="mdi-office-building"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.refType"
            :items="departmentsForFilter"
            item-text="name"
            item-value="id"
            label="Department"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onDeptChange"
            prepend-icon="mdi-account-group"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.subRefType"
            :items="filteredSubRefTypes"
            item-text="subCode"
            item-value="id"
            label="File Classification"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            :hint="'Select Department first to enable File Classification.'"
            :disabled="!filters.refType"
            @change="onFilterChange"
            prepend-icon="mdi-file-document-outline"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.adjuster"
            :items="filteredAdjusters"
            item-text="username"
            item-value="id"
            label="Adjuster"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            :hint="'Select Branch first to enable Adjuster.'"
            :disabled="!filters.branch"
            @change="onFilterChange"
            prepend-icon="mdi-account-tie"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            clearable
            v-model="filters.fileStatus"
            :items="stagesForFilter"
            item-text="name"
            item-value="stageCode"
            label="Status"
            :menu-props="{ maxHeight: '400' }"
            persistent-hint
            @change="onFilterChange"
            prepend-icon="mdi-checkbox-marked-circle-outline"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            clearable
            v-model="filters.vehicleNo"
            label="Vehicle No."
            @change="onFilterChange"
            prepend-icon="mdi-car"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            clearable
            v-model="filters.id"
            label="AA Ref."
            @change="onFilterChange"
            prepend-icon="mdi-numeric"
            dense
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-menu v-model="menuStart" :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.startDate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="filters.startDate"
              @change="
                menuStart = false;
                onFilterChange();
              "
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="2">
          <v-menu v-model="menuEnd" :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.endDate"
                label="End Date"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="filters.endDate"
              @change="
                menuEnd = false;
                onFilterChange();
              "
            />
          </v-menu>
        </v-col>
      </v-row>
    </v-card>

    <v-row>
      <v-col cols="12">
        <div
          class="pagination-bar d-flex align-center justify-space-between mb-2 flex-wrap"
        >
          <div class="pagination-controls d-flex align-center flex-wrap">
            <v-select
              :items="[30, 50, 100, 500]"
              v-model="pageSize"
              label="Rows"
              dense
              hide-details
              style="max-width: 90px"
              @change="onPageSizeChange"
              class="pagination-select"
            ></v-select>
            <v-btn
              icon
              :disabled="paginatedPage === 1"
              @click="goToFirstPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-page-first</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="paginatedPage === 1"
              @click="goToPrevPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <template v-for="btn in paginationButtons">
              <v-btn
                v-if="btn.type === 'page'"
                :key="btn.page"
                small
                :color="btn.page === paginatedPage ? 'primary' : ''"
                @click="onPageChange(btn.page)"
                class="mx-1 pagination-btn"
              >
                {{ btn.page }}
              </v-btn>
              <span
                v-else-if="btn.type === 'ellipsis'"
                :key="btn.key"
                class="mx-1 pagination-ellipsis"
                >...</span
              >
            </template>
            <v-btn
              icon
              :disabled="paginatedPage === totalPages"
              @click="goToNextPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="paginatedPage === totalPages"
              @click="goToLastPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-page-last</v-icon>
            </v-btn>
            <v-text-field
              v-model.number="jumpToPage"
              label="Go to"
              type="number"
              dense
              hide-details
              style="max-width: 60px"
              min="1"
              :max="totalPages"
              @keyup.enter="jumpPage"
              class="ml-2 pagination-jump"
            ></v-text-field>
          </div>
          <span class="ml-2 pagination-total">
            {{ paginatedTotal }} total records
          </span>
        </div>
        <v-data-table
          dense
          :headers="headers"
          :items="paginatedCasefiles"
          :item-class="itemClass"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :loading="paginatedLoading"
          :search="search"
          :items-per-page="pageSize"
          class="elevation-1"
          mobile-breakpoint="1100"
          @click:row="viewItem"
          style="cursor: pointer"
          hide-default-footer
        >
          <template slot="item.numbering" slot-scope="props">
            {{ (paginatedPage - 1) * pageSize + props.index + 1 }}
          </template>

          <template v-slot:[`item.vehicleNo`]="{ item }">
            <span v-if="item.vehicleNo">{{
              item.vehicleNo.toUpperCase()
            }}</span>
            <span v-else>TBA</span>
          </template>

          <template v-slot:[`item.id`]="{ item }">
            AA
            <span
              v-for="dept in filterDept(item.refType)"
              :key="`dept-${dept.id}`"
            >
              /{{ dept.name }}
              <span
                v-for="subDept in filterSubDept(item.subRefType)"
                :key="`subDept-${subDept.id}`"
              >
                /{{ subDept.subCode }}
              </span>
            </span>
            /{{ item.id }}/{{ yearOnly(item.dateOfAssign) }}
            <span
              v-for="branch in filterBranches(item.branch)"
              :key="`branch-${branch.id}`"
            >
              /{{ branch.brCode }}
            </span>
          </template>

          <template v-slot:[`item.insurer`]="{ item }">
            <div v-for="ins in filterIns(item.insurer)" :key="ins.id">
              <div class="text-truncate" style="max-width: 100px">
                {{ ins.name }}
              </div>
            </div>
          </template>

          <template v-slot:[`item.dateOfAssign`]="{ item }">
            {{ formatDate(item.dateOfAssign) }}
          </template>

          <template v-slot:[`item.days`]="{ item }">
            {{ dateDiff(item.dateOfAssign, new Date()) }}
          </template>

          <template v-slot:[`item.adjuster`]="{ item }">
            <span v-if="item.adjuster">
              <div
                v-for="adjuster in filterUser(item.adjuster)"
                :key="adjuster.id"
              >
                {{ adjuster.username }}
              </div>
            </span>
            <span v-else>TBA</span>
          </template>

          <template v-slot:[`item.fileStatus`]="{ item }">
            <v-chip label outlined block x-small>
              {{ statusName(item.fileStatus) }}
            </v-chip>
          </template>
        </v-data-table>

        <div
          class="pagination-bar d-flex align-center justify-space-between mt-2 flex-wrap"
        >
          <div class="pagination-controls d-flex align-center flex-wrap">
            <v-select
              :items="[30, 50, 100, 500]"
              v-model="pageSize"
              label="Rows"
              dense
              hide-details
              style="max-width: 90px"
              @change="onPageSizeChange"
              class="pagination-select"
            ></v-select>
            <v-btn
              icon
              :disabled="paginatedPage === 1"
              @click="goToFirstPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-page-first</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="paginatedPage === 1"
              @click="goToPrevPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <template v-for="btn in paginationButtons">
              <v-btn
                v-if="btn.type === 'page'"
                :key="btn.page"
                small
                :color="btn.page === paginatedPage ? 'primary' : ''"
                @click="onPageChange(btn.page)"
                class="mx-1 pagination-btn"
              >
                {{ btn.page }}
              </v-btn>
              <span
                v-else-if="btn.type === 'ellipsis'"
                :key="btn.key"
                class="mx-1 pagination-ellipsis"
                >...</span
              >
            </template>
            <v-btn
              icon
              :disabled="paginatedPage === totalPages"
              @click="goToNextPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              icon
              :disabled="paginatedPage === totalPages"
              @click="goToLastPage"
              class="pagination-btn"
            >
              <v-icon small>mdi-page-last</v-icon>
            </v-btn>
            <v-text-field
              v-model.number="jumpToPage"
              label="Go to"
              type="number"
              dense
              hide-details
              style="max-width: 60px"
              min="1"
              :max="totalPages"
              @keyup.enter="jumpPage"
              class="ml-2 pagination-jump"
            ></v-text-field>
          </div>
          <span class="ml-2 pagination-total">
            {{ paginatedTotal }} total records
          </span>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import InsDataService from "@/services/InsDataService";
import MemberDataService from "@/services/MemberDataService";
import BranchDataService from "@/services/BranchDataService";
import DeptDataService from "@/services/DeptDataService";
import SubRefTypeDataService from "@/services/SubDeptDataService";

export default {
  name: "Archive",
  data: () => ({
    search: "",
    addDialog: false,
    pageSize: 30,
    jumpToPage: null,
    sortBy: ["days"],
    sortDesc: [true],
    insurer: [],
    members: [],
    branches: [],
    refType: [],
    subRefTypes: [],
    filters: {
      insurer: "",
      branch: "",
      refType: "",
      subRefType: "",
      adjuster: "",
      fileStatus: "",
      vehicleNo: "",
      id: "",
      startDate: null,
      endDate: null,
    },
    menuStart: false,
    menuEnd: false,
    localPaginatedPage: 1,
    headers: [
      { text: "NO.", align: "start", sortable: false, value: "numbering" },
      {
        text: "VEHICLE NO.",
        align: "center",
        value: "vehicleNo",
        sortable: false,
      },
      { text: "AA REF", align: "start", value: "id", sortable: false },
      { text: "INSURER", align: "center", value: "insurer", sortable: false },
      { text: "DOA", align: "start", sortable: false, value: "dateOfAssign" },
      { text: "DAYS", align: "start", sortable: false, value: "days" },
      { text: "ADJUSTER", align: "start", sortable: false, value: "adjuster" },
      { text: "STATUS", align: "center", value: "fileStatus", sortable: false },
    ],
  }),
  computed: {
    ...mapGetters([
      "getPaginatedPermittedArchive",
      "getPaginatedTotalPermittedArchive",
      "getPaginatedPermittedArchiveLoading",
    ]),
    paginatedCasefiles() {
      return this.getPaginatedPermittedArchive;
    },
    paginatedTotal() {
      return this.getPaginatedTotalPermittedArchive;
    },
    paginatedPage: {
      get() {
        return this.localPaginatedPage;
      },
      set(val) {
        this.setPage(val);
      },
    },
    paginatedLoading() {
      return this.getPaginatedPermittedArchiveLoading;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.paginatedTotal / this.pageSize));
    },
    paginationButtons() {
      const pages = [];
      const total = this.totalPages;
      const current = this.paginatedPage;
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push({ type: "page", page: i });
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push({ type: "page", page: i });
          pages.push({ type: "ellipsis", key: "e1" });
          pages.push({ type: "page", page: total });
        } else if (current >= total - 3) {
          pages.push({ type: "page", page: 1 });
          pages.push({ type: "ellipsis", key: "e2" });
          for (let i = total - 4; i <= total; i++)
            pages.push({ type: "page", page: i });
        } else {
          pages.push({ type: "page", page: 1 });
          pages.push({ type: "ellipsis", key: "e3" });
          for (let i = current - 1; i <= current + 1; i++)
            pages.push({ type: "page", page: i });
          pages.push({ type: "ellipsis", key: "e4" });
          pages.push({ type: "page", page: total });
        }
      }
      return pages;
    },
    addCasefileDisabled() {
      // Always allow add for now, or add your logic here
      return false;
    },
    insurersForFilter() {
      if (!this.insurer || this.insurer.length === 0) return [];
      return [...this.insurer]
        .map((ins) => ({
          ...ins,
          name: ins.name ? ins.name.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    branchesForFilter() {
      if (!this.branches || this.branches.length === 0) return [];
      const user = this.$store.state.auth.user;
      if (!user) return [];
      if (
        user.roles &&
        user.roles.some((role) => ["ADMIN", "MANAGER", "CLERK"].includes(role))
      ) {
        return [...this.branches]
          .map((b) => ({
            ...b,
            name: b.name ? b.name.trim().toUpperCase() : "",
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      }
      const userBranches = Array.isArray(user.branches)
        ? user.branches
        : [user.branches];
      return this.branches
        .filter((branch) =>
          userBranches.some(
            (userBranch) =>
              userBranch === branch.id ||
              userBranch === Number(branch.id) ||
              userBranch === String(branch.id)
          )
        )
        .map((b) => ({
          ...b,
          name: b.name ? b.name.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    departmentsForFilter() {
      if (!this.refType || this.refType.length === 0) return [];
      const user = this.$store.state.auth.user;
      if (!user) return [];
      if (
        user.roles &&
        user.roles.some((role) => ["ADMIN", "MANAGER", "CLERK"].includes(role))
      ) {
        return [...this.refType]
          .map((dept) => ({
            ...dept,
            name: dept.name ? dept.name.trim().toUpperCase() : "",
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      }
      const userDepts = Array.isArray(user.dept) ? user.dept : [user.dept];
      return this.refType
        .filter((dept) =>
          userDepts.some(
            (userDept) =>
              userDept === dept.id ||
              userDept === Number(dept.id) ||
              userDept === String(dept.id)
          )
        )
        .map((dept) => ({
          ...dept,
          name: dept.name ? dept.name.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    stagesForFilter() {
      // Only show closed/cancelled statuses
      return [
        { stageCode: "all", name: "All Statuses" },
        { stageCode: "CLO", name: "Closed" },
        { stageCode: "CANC", name: "Cancelled" },
      ];
    },
    filteredSubRefTypes() {
      if (!this.filters.refType) return [];
      return this.subRefTypes
        .filter(
          (sub) =>
            sub.deptId === this.filters.refType ||
            sub.deptId === Number(this.filters.refType) ||
            sub.deptId === String(this.filters.refType)
        )
        .sort((a, b) => a.subCode.localeCompare(b.subCode));
    },
    filteredAdjusters() {
      if (!this.filters.branch) return [];
      const branchObj = this.branches.find(
        (b) =>
          b.id === this.filters.branch ||
          b.id === Number(this.filters.branch) ||
          b.id === String(this.filters.branch)
      );
      if (!branchObj) return [];
      const branchName = branchObj.name;
      return this.members
        .filter(
          (adj) =>
            adj.active === true &&
            Array.isArray(adj.roles) &&
            adj.roles.some((r) => r.roleCode === "adjuster") &&
            Array.isArray(adj.branches) &&
            adj.branches.some((b) => b.name === branchName)
        )
        .map((adj) => ({
          ...adj,
          username: adj.username ? adj.username.trim().toUpperCase() : "",
        }))
        .sort((a, b) => a.username.trim().localeCompare(b.username.trim()));
    },
  },
  methods: {
    async fetchPaginated() {
      const filters = {};
      Object.keys(this.filters).forEach((key) => {
        if (key === "fileStatus" && this.filters.fileStatus === "all") {
          return;
        }
        if (
          this.filters[key] !== null &&
          this.filters[key] !== "" &&
          typeof this.filters[key] !== "undefined"
        ) {
          filters[key] = this.filters[key];
        }
      });
      if (
        filters.adjuster &&
        typeof filters.adjuster === "object" &&
        filters.adjuster.id
      ) {
        filters.adjuster = filters.adjuster.id;
      }
      if (filters.id) {
        filters.id = Number(filters.id);
      }
      await this.$store.dispatch("casefiles/fetchPaginatedPermittedArchive", {
        page: this.paginatedPage,
        pageSize: this.pageSize,
        filters,
      });
    },
    onPageChange(page) {
      this.$store.dispatch("casefiles/fetchPaginatedPermittedArchive", {
        page,
        pageSize: this.pageSize,
        filters: this.filters,
      });
      this.localPaginatedPage = page;
    },
    onPageSizeChange() {
      this.$store.dispatch("casefiles/fetchPaginatedPermittedArchive", {
        page: 1,
        pageSize: this.pageSize,
        filters: this.filters,
      });
      this.localPaginatedPage = 1;
    },
    onFilterChange() {
      if (this.filters.fileStatus === "all") {
        this.filters.fileStatus = "";
      }
      this.localPaginatedPage = 1;
      this.fetchPaginated();
    },
    onBranchChange() {
      this.filters.adjuster = "";
      this.onFilterChange();
    },
    onDeptChange() {
      this.filters.subRefType = "";
      this.onFilterChange();
    },
    setPage(page) {
      if (page !== this.localPaginatedPage) {
        this.localPaginatedPage = page;
      }
      this.onPageChange(page);
    },
    goToFirstPage() {
      if (this.paginatedPage > 1) {
        this.onPageChange(1);
      }
    },
    goToPrevPage() {
      if (this.paginatedPage > 1) {
        this.onPageChange(this.paginatedPage - 1);
      }
    },
    goToNextPage() {
      if (this.paginatedPage < this.totalPages) {
        this.onPageChange(this.paginatedPage + 1);
      }
    },
    goToLastPage() {
      if (this.paginatedPage < this.totalPages) {
        this.onPageChange(this.totalPages);
      }
    },
    jumpPage() {
      if (
        this.jumpToPage &&
        this.jumpToPage >= 1 &&
        this.jumpToPage <= this.totalPages
      ) {
        this.onPageChange(this.jumpToPage);
        this.jumpToPage = null;
      }
    },
    viewItem(item) {
      this.$router.push({
        name: "vuexFile",
        params: { id: item.id },
      });
    },
    itemClass() {
      // Optionally implement TAT coloring logic here
      return "";
    },
    statusName(code) {
      if (code === "CLO") return "CLOSED";
      if (code === "CANC") return "CANCELLED";
      return code;
    },
    async loadInsurersAndMembers() {
      try {
        const [insurerRes, membersRes] = await Promise.all([
          InsDataService.getAll(),
          MemberDataService.getAll(),
        ]);
        this.insurer = insurerRes.data;
        this.members = membersRes.data;
      } catch (e) {
        // handle error if needed
      }
    },
    loadLookups() {
      // Load branches, departments, and sub-departments
      return Promise.all([
        BranchDataService.getAll(),
        DeptDataService.getAll(),
        SubRefTypeDataService.getAll(),
      ]).then(([branchesRes, refTypeRes, subRefTypesRes]) => {
        this.branches = branchesRes.data;
        this.refType = refTypeRes.data;
        this.subRefTypes = subRefTypesRes.data;
      });
      // Removed catch block with unused 'e'
    },
    filterDept(id) {
      if (!id || !this.refType.length) return [];
      return this.refType.filter((dept) => dept.id === id);
    },
    filterSubDept(id) {
      if (!id || !this.subRefTypes.length) return [];
      return this.subRefTypes.filter((subDept) => subDept.id === id);
    },
    filterBranches(id) {
      if (!id || !this.branches.length) return [];
      return this.branches.filter((branch) => branch.id === id);
    },
    filterIns(id) {
      if (!id || !this.insurer.length) return [];
      return this.insurer.filter((ins) => ins.id === id);
    },
    filterUser(id) {
      if (!id || !this.members.length) return [];
      return this.members.filter((member) => member.id === id);
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-GB");
    },
    yearOnly(date) {
      if (!date) return "";
      return new Date(date).getFullYear().toString().slice(-2);
    },
    dateDiff(startDate, endDate) {
      if (!startDate) return "";
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    },
    clearFilters() {
      this.filters = {
        insurer: "",
        branch: "",
        refType: "",
        subRefType: "",
        adjuster: "",
        fileStatus: "",
        vehicleNo: "",
        id: "",
        startDate: null,
        endDate: null,
      };
      this.localPaginatedPage = 1;
      this.fetchPaginated();
    },
  },
  watch: {
    $route(to) {
      if (to.path === "/archive") {
        this.fetchPaginated();
        this.localPaginatedPage = 1;
      }
    },
    // Sync local page with store if needed
    getPaginatedPermittedArchivePage(val) {
      this.localPaginatedPage = val;
    },
    localPaginatedPage(val) {
      // When local changes, update store if needed
      if (val !== this.getPaginatedPermittedArchivePage) {
        this.onPageChange(val);
      }
    },
  },
  async mounted() {
    this.localPaginatedPage = 1;
    await this.loadInsurersAndMembers();
    await this.loadLookups();
    await this.fetchPaginated();
  },
};
</script>
<style>
/* ...existing code... */
</style>
